import { Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { Home, ArrowLeft, MapPin, Compass } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function NotFound() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-2xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          {/* 404 Number with gradient */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <h1 className="text-8xl md:text-9xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700">
              404
            </h1>
            {/* Floating compass icon */}
            <motion.div
              animate={{
                rotate: 360,
                y: [0, -10, 0],
              }}
              transition={{
                rotate: { duration: 8, repeat: Infinity, ease: 'linear' },
                y: { duration: 3, repeat: Infinity, ease: 'easeInOut' },
              }}
              className="absolute -top-4 -right-4 text-amber-500/30"
            >
              <Compass className="h-16 w-16" />
            </motion.div>
          </motion.div>

          {/* Main message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-4"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">
              Oops! You've wandered off the map
            </h2>
            <p className="text-lg text-muted-foreground max-w-md mx-auto">
              The page you're looking for doesn't exist. Let's get you back on
              track to plan your next adventure.
            </p>
          </motion.div>

          {/* Decorative travel icons */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex justify-center space-x-8 text-muted-foreground/30"
          >
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0 }}
            >
              <MapPin className="h-8 w-8" />
            </motion.div>
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
            >
              <Compass className="h-8 w-8" />
            </motion.div>
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 1 }}
            >
              <Home className="h-8 w-8" />
            </motion.div>
          </motion.div>

          {/* Action buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to="/"
              search={{
                tripType: 'weekend',
                destination: 'new-york-ny',
                interest: 'family_vacation',
              }}
            >
              <Button size="lg" className="min-w-[160px]">
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
            </Link>
            <Button
              variant="outline"
              size="lg"
              onClick={() => window.history.back()}
              className="min-w-[160px]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </motion.div>

          {/* Helpful links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="pt-8"
          >
            <p className="text-sm text-muted-foreground mb-4">
              Or explore these popular sections:
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/trips"
                className="text-sm text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 transition-colors"
              >
                My Trips
              </Link>
              <span className="text-muted-foreground">•</span>
              <Link
                to="/profile"
                className="text-sm text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 transition-colors"
              >
                Profile
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
